/**
 * App.js
 * Tatalli Cloud — Ana Router
 *
 * Güvenlik & Performans Notları:
 *  - PUBLIC_ROUTES  → statik import (SEO için hızlı yükleme)
 *  - PORTAL_ROUTES  → lazy import, Session'a basePath="/portal" geçer
 *  - CA_ROUTES      → lazy import, Session'a basePath="/ca" geçer
 */

import './App.css';
import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationStream from './contexts/NotificationStream';
import Session from './Components/Session';
import { initTokenManager } from './Services/apiClient';

/* ─── PUBLIC Sayfalar — Statik Import ─────────────────────────────────────*/
import HomePage                            from './Pages/Public/Static/HomePage';
import ProtectionOfPersonalData            from './Pages/Public/Static/ProtectionOfPersonalData';
import Agreements                          from './Pages/Public/Static/Agreements';
import {
  PricingPage, ContactPage, SalesPage, StatusPage,
  PrivacyPage, TermsPage, SlaPage, LegalPage, AboutPage, SupportPage,
  PricingPurchaseOptionsPage, ProductSaaSCorviaPage,
  ProductInfrastructureCloudInstancePage,
} from './Pages/Public/Static/StaticPages';
import Login           from './Pages/Member/Login';
import ResetPassword1  from './Pages/Member/ResetPassword1';
import ResetPassword2  from './Pages/Member/ResetPassword2';
import Logout          from './Pages/Member/Logout';
import ChangedLanguage from './Pages/ChangedLanguage';

/* ─── PORTAL Sayfaları — Lazy Import ──────────────────────────────────────*/
const Dashboard               = lazy(() => import('./Pages/Dashboard'));
const BillingIndex            = lazy(() => import('./Pages/Billing/Billing'));
const BillingAddBalance       = lazy(() => import('./Pages/Billing/AddBalance'));
const BillingPaymentPending   = lazy(() => import('./Pages/Billing/PaymentPending'));
const BillingPaymentSuccess   = lazy(() => import('./Pages/Billing/PaymentSuccess'));
const BillingPaymentFail      = lazy(() => import('./Pages/Billing/PaymentFail'));
const BillingPaymentFailure   = lazy(() => import('./Pages/Billing/PaymentFailure'));
const Settings                = lazy(() => import('./Pages/Settings'));
const BlankPage               = lazy(() => import('./Pages/BlankPage'));
const SaaS_CorviaProvisioningWizard             = lazy(() => import('./Pages/SaaS/CorviaProvisioningWizard'));
const Subscriptions_Corvia           = lazy(() => import('./Pages/Subscriptions/Corvia'));
const Infrastructure_TatalliDirectory = lazy(() => import('./Pages/Infrastructure/IdentityManagement/TatalliDirectory'));
const Infrastructure_TatalliIAM       = lazy(() => import('./Pages/Infrastructure/IdentityManagement/TatalliIAM'));
const Infrastructure_FtpStorage       = lazy(() => import('./Pages/Infrastructure/Storages/FtpStorage'));
const Infrastructure_S3Storage        = lazy(() => import('./Pages/Infrastructure/Storages/S3Storage'));
const Infrastructure_PostgresDb       = lazy(() => import('./Pages/Infrastructure/Databases/Postgres'));
const Infrastructure_ArtemisMq        = lazy(() => import('./Pages/Infrastructure/Messaging/ArtemisMq'));

/* ─── CA Sayfaları — Lazy Import ───────────────────────────────────────── */
const CaDashboard                       = lazy(() => import('./Pages/Ca/Dashboard'));
const CaFinanceCustomerSearch           = lazy(() => import('./Pages/Ca/Finance/CustomerSearch'));
const CaFinanceCustomerDetails           = lazy(() => import('./Pages/Ca/Finance/CustomerDetails'));

/* ── Yükleniyor Ekranı ─────────────────────────────────────────────────── */
function PortalFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <span>Yükleniyor…</span>
    </div>
  );
}

/* ── Rotalar ───────────────────────────────────────────────────────────── */
const PUBLIC_ROUTES = [
  { path: '/',                                               Component: HomePage                               },
  { path: '/pricing',                                        Component: PricingPage                            },
  { path: '/contact',                                        Component: ContactPage                            },
  { path: '/sales',                                          Component: SalesPage                              },
  { path: '/status',                                         Component: StatusPage                             },
  { path: '/privacy',                                        Component: PrivacyPage                            },
  { path: '/terms',                                          Component: TermsPage                              },
  { path: '/sla',                                            Component: SlaPage                                },
  { path: '/legal',                                          Component: LegalPage                              },
  { path: '/about',                                          Component: AboutPage                              },
  { path: '/support',                                        Component: SupportPage                            },
  { path: '/protection-of-personal-data',                    Component: ProtectionOfPersonalData               },
  { path: '/agreements',                                     Component: Agreements                             },
  { path: '/product/saas/corvia',                            Component: ProductSaaSCorviaPage                  },
  { path: '/product/infrastructure/cloud-instance',          Component: ProductInfrastructureCloudInstancePage },
  { path: '/pricing/purchase-options',                       Component: PricingPurchaseOptionsPage             },
  { path: '/portal/changed-language',                        Component: ChangedLanguage                        },
  { path: '/portal/login',                                   Component: Login                                  },
  { path: '/portal/reset-password-1',                        Component: ResetPassword1                         },
  { path: '/portal/reset-password-2',                        Component: ResetPassword2                         },
  { path: '/portal/logout',                                  Component: Logout                                 },
];

const PORTAL_ROUTES = [
  { path: '/portal/',                                                     Component: Session                        },
  { path: '/portal/dashboard',                                            Component: Dashboard                      },
  { path: '/portal/billing',                                              Component: BillingIndex                   },
  { path: '/portal/billing/add-balance',                                  Component: BillingAddBalance              },
  { path: '/portal/billing/payment-pending',                              Component: BillingPaymentPending          },  
  { path: '/portal/billing/payment-success',                              Component: BillingPaymentSuccess          },
  { path: '/portal/billing/payment-fail',                                 Component: BillingPaymentFail             },    
  { path: '/portal/billing/payment-failure',                              Component: BillingPaymentFailure          },    
  { path: '/portal/settings',                                             Component: Settings                       },
  { path: '/portal/blank',                                                Component: BlankPage                      },
  { path: '/portal/subscriptions/corvia/:instanceLabel',                  Component: Subscriptions_Corvia           },  
  { path: '/portal/saas/corvia-provisioning-wizard',                      Component: SaaS_CorviaProvisioningWizard  },
  { path: '/portal/infrastructure/identity-management/tatalli-directory', Component: Infrastructure_TatalliDirectory},
  { path: '/portal/infrastructure/identity-management/tatalli-iam',       Component: Infrastructure_TatalliIAM      },
  { path: '/portal/infrastructure/storage/ftp-storage',                   Component: Infrastructure_FtpStorage      },
  { path: '/portal/infrastructure/storage/s3',                            Component: Infrastructure_S3Storage       },
  { path: '/portal/infrastructure/databases/postgres',                    Component: Infrastructure_PostgresDb      },
  { path: '/portal/infrastructure/messaging/artemis-mq',                  Component: Infrastructure_ArtemisMq       },
];

const CA_ROUTES = [
  { path: '/portal/ca/dashboard',                                            Component: CaDashboard                      },
  { path: '/portal/ca/finance/customer-search',                              Component: CaFinanceCustomerSearch          },  
  { path: '/portal/ca/finance/customers/:customerId',                        Component: CaFinanceCustomerDetails         },    
];

/* ── App ───────────────────────────────────────────────────────────────── */
function App() {
  React.useEffect(() => { initTokenManager(); }, []);

  return (
    <NotificationProvider>
      <NotificationStream />
      <div className="App">
        <Routes>
          {/* Public route'lar */}
          {PUBLIC_ROUTES.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}

          {/* /portal route'ları */}
          {PORTAL_ROUTES.map(({ path, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <Session basePath="/portal">
                  <Suspense fallback={<PortalFallback />}>
                    <Component />
                  </Suspense>
                </Session>
              }
            />
          ))}

          {/* /ca route'ları */}
          {CA_ROUTES.map(({ path, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <Session basePath="/ca">
                  <Suspense fallback={<PortalFallback />}>
                    <Component />
                  </Suspense>
                </Session>
              }
            />
          ))}
        </Routes>
      </div>
    </NotificationProvider>
  );
}

export default App;